<template>
  <vs-popup ref="popupExcelDownload" id="popupExcelDownload" title="Excel 다운로드" :active.sync="showPopup" :style="{ zIndex: this.zIndex || 53000 }">
    <div class="excel-popup-body">
      <!-- 미리보기 테이블 -->
      <div class="table-scroll-wrap">
        <table class="preview-table">
          <thead v-if="showHeader">
            <tr>
              <th v-for="(key, index) in getKeys" :key="index">{{ key }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in convertList" :key="index">
              <td v-for="(key, kIndex) in getKeys" :key="kIndex">{{ item[key] }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 하단 다운로드 바 -->
      <div class="download-bar">
        <div class="filename-area">
          <span class="filename-label">파일 이름</span>
          <div class="filename-input-wrap">
            <input class="filename-input" type="text" v-model="fileName" :placeholder="getTitle" maxlength="150" @keyup.enter="excelDownFunc" />
            <span class="filename-ext">.xlsx</span>
          </div>
        </div>
        <vs-button color="secondary" @click="excelDownFunc">다운로드</vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import XLSX from "xlsx";

export default {
  name: "PopupExcelDownload",
  props: {
    value: Boolean,
    list: Array,
    title: String,
    showHeader: {
      type: Boolean,
      default: true,
    },
    zIndex: [Number, String],
    // 기본 파일명(확장자 제외). 지정되지 않으면 날짜 + title 조합을 사용한다.
    defaultFileName: { type: String, default: "" },
    // 다운로드 버튼 클릭 시 호출할 외부 함수. 지정되면 내부 XLSX 작성 로직 대신 이 함수가 실행된다.
    downloadFunc: { type: Function, default: null },
  },
  computed: {
    convertList() {
      const convert = JSON.parse(JSON.stringify(this.list).replace(/<[^>]*>/g, ""));

      if (convert && convert.length > 0) {
        convert.forEach((item) => {
          Object.keys(item).forEach((key) => {
            try {
              if (typeof item[key] === "string") {
                item[key] = item[key].replace(/<[^>]*>/g, "");
              } else if (item[key] && typeof item[key] === "object") {
                if (item[key].text) item[key] = item[key].text;
                else if (item[key].html) item[key] = item[key].html;
                else if (item[key].files) item[key] = item[key].files.map((file) => (file.name || file.fileName || "").replace(/\.[^/.]+$/, "")).join(", ");
                else if (item[key].type && item[key].type === "checkbox") {
                  item[key] = item[key].value ? "Y" : "N";
                }
              }
            } catch (e) {
              console.error(`Error processing key ${key}:`, e);
            }
          });
        });
      }

      return convert;
    },
    getKeys() {
      if (this.list && this.list.length > 0) {
        const _keys = Object.keys(this.list[0]);
        const keys = [];
        for (const key of _keys) {
          if (key !== "postId" && key !== "projId" && key !== "raw" && key !== "첨부문서") {
            keys.push(key.replace(/<[^>]*>/g, ""));
          }
        }
        return keys;
      }
      return [];
    },
    getTitle() {
      if (this.defaultFileName) {
        return this.defaultFileName;
      }
      if (this.title) {
        return `${new Date().format("yyyy-MM-dd")}_${this.title}`;
      } else {
        return new Date().format("yyyy-MM-dd");
      }
    },
    // 실제 저장에 사용할 파일명(확장자 제외). 사용자가 입력한 값이 없으면 기본 파일명을 사용한다.
    downloadFileName() {
      // 파일명으로 사용할 수 없는 문자와 끝의 .xlsx 확장자를 제거
      const name = (this.fileName || "")
        .replace(/[\\/:*?"<>|]/g, "")
        .replace(/\.xlsx$/i, "")
        .trim();
      return name || this.getTitle;
    },
  },
  watch: {
    value(data) {
      this.showPopup = data;
    },
    showPopup(data) {
      this.$emit("input", data);
      // 팝업을 열 때마다 기본 파일명으로 초기화
      if (data) this.fileName = this.getTitle;
    },
  },
  data() {
    return {
      showPopup: false,
      fileName: "",
    };
  },

  mounted() {
    const backButtonRouteGuard = this.$router.beforeEach((to, from, next) => {
      if (this.showPopup) {
        this.showPopup = false;
        next(false);
        return;
      }
      next(true);
    });
    this.$once("hook:destroyed", () => {
      backButtonRouteGuard();
    });
  },

  created() {
    this.showPopup = this.value;
    this.fileName = this.getTitle;
  },

  methods: {
    excelDownFunc() {
      const fileName = this.downloadFileName;
      if (this.downloadFunc) {
        // 외부 다운로드 함수에도 사용자가 지정한 파일명(확장자 제외)을 전달한다.
        this.downloadFunc(fileName);
        this.showPopup = false;
        return;
      }
      const dataWS = XLSX.utils.json_to_sheet(this.convertList);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, dataWS, "nameData");
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    },
  },
};
</script>

<style>
#popupExcelDownload > .vs-popup {
  width: min(95vw, 1400px) !important;
}

#popupExcelDownload > .vs-popup > .vs-popup--content {
  width: 100%;
  padding: 0;
  margin: 0;
}
</style>

<style scoped>
.excel-popup-body {
  display: flex;
  flex-direction: column;
  height: 70vh;
}

/* 미리보기 테이블 스크롤 영역 */
.table-scroll-wrap {
  flex: 1;
  overflow: auto;
  border-bottom: 1px solid #e0e0e0;
}

.preview-table {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
}

.preview-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.preview-table th {
  background: #f0f2f5;
  font-size: 13px;
  font-weight: 600;
  color: #444;
  text-align: center;
  white-space: nowrap;
  padding: 10px 14px;
  border: 1px solid #d0d4dc;
}

.preview-table td {
  font-size: 13px;
  color: #333;
  text-align: center;
  white-space: nowrap;
  padding: 8px 14px;
  border: 1px solid #e0e0e0;
  background: #fff;
}

.preview-table tbody tr:nth-child(even) td {
  background: #fafafa;
}

.preview-table tbody tr:hover td {
  background: #eef3ff;
}

/* 하단 다운로드 바 */
.download-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: #f9f9f9;
  gap: 16px;
  flex-shrink: 0;
}

.filename-area {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  flex: 1;
}

.filename-label {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
  font-weight: 500;
}

.filename-input-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border: 1px solid #d0d4dc;
  border-radius: 4px;
  padding: 0 12px;
  flex: 1;
  overflow: hidden;
}

.filename-input-wrap:focus-within {
  border-color: #7367f0;
}

.filename-input {
  font-size: 13px;
  color: #333;
  border: none;
  outline: none;
  background: transparent;
  padding: 6px 0;
  flex: 1;
  min-width: 0;
}

.filename-ext {
  font-size: 13px;
  color: #888;
  white-space: nowrap;
}
</style>
